/**
 * TaskSense authority vocabulary.
 *
 * NUC-RBAC v1→v2 (TASK-RBAC): the UI no longer gates on the coarse
 * UPPERCASE_SNAKE authorities (USERS_MANAGEMENT, MANAGE_SCHEDULERS, …) nor on
 * ROLE_ADMIN alone. Every product/admin screen and EVERY action button now
 * resolves a real `entity:action` capability against the user's expanded
 * authority list (from /oauth2/me → ApplicationConfigService.getAuthorities),
 * which already carries the dependency-closure read caps.
 *
 * The coarse roles survive ONLY as the universal escape hatch — ROLE_ADMIN
 * (the tasksense super-admin) and ROLE_DEVOPS (the ops console) — mirroring
 * the nucleus group→authority grant model and the bookwise-ui CapabilityChecker
 * pattern. hasCapability() ORs the requested cap with these two roles. All
 * write/mutation gates resolve the literal `entity:action` cap; no gate ever
 * falls back to a coarse UPPER_SNAKE authority.
 *
 * Entities (v2 catalog):
 *   userdata        — user-data rows           (scheduling/data screen)
 *   userdata-type   — user-data type configs   (administration/userdata-types)
 *   job             — schedules / scheduled jobs
 *   scheduler       — schedule editor / running schedule control
 *   task-definition — the task catalog          (scheduling/tasks)
 *   runner          — runner registry           (administration/runners)
 *   capability      — capability registry       (administration/capabilities)
 *   datasource      — data sources
 * Actions: access | list | view | create | update | delete | control | run | report
 *
 * Nucleus-lib admin screens (users, access-request, audit, mail, mail-template,
 * reference-data, connections, org-unit, system-property) keep their existing
 * nucleus-owned colon caps — those are shared verbatim across tenants.
 */
export abstract class RoleConstants {

    // -- Roles (escape hatches only) -----------------------------------------
    static readonly ROLE_ADMIN   = "ROLE_ADMIN";
    static readonly ROLE_DEVOPS  = "ROLE_DEVOPS";
    static readonly ROLE_USER    = "ROLE_USER";

    // -- User data (entity `userdata`) ---------------------------------------
    static readonly USERDATA_ACCESS  = "userdata:access";
    static readonly USERDATA_LIST    = "userdata:list";
    static readonly USERDATA_VIEW    = "userdata:view";
    static readonly USERDATA_CREATE  = "userdata:create";
    static readonly USERDATA_UPDATE  = "userdata:update";
    static readonly USERDATA_DELETE  = "userdata:delete";

    // -- User data types (entity `userdata-type`) ----------------------------
    static readonly USERDATA_TYPE_ACCESS = "userdata-type:access";
    static readonly USERDATA_TYPE_LIST   = "userdata-type:list";
    static readonly USERDATA_TYPE_VIEW   = "userdata-type:view";
    static readonly USERDATA_TYPE_CREATE = "userdata-type:create";
    static readonly USERDATA_TYPE_UPDATE = "userdata-type:update";
    static readonly USERDATA_TYPE_DELETE = "userdata-type:delete";

    // -- Schedules / jobs (entity `job`) -------------------------------------
    static readonly JOB_ACCESS  = "job:access";
    static readonly JOB_LIST    = "job:list";
    static readonly JOB_VIEW    = "job:view";
    static readonly JOB_CREATE  = "job:create";
    static readonly JOB_UPDATE  = "job:update";
    static readonly JOB_DELETE  = "job:delete";
    static readonly JOB_CONTROL = "job:control";
    static readonly JOB_RUN     = "job:run";

    // -- Scheduler control (entity `scheduler`) ------------------------------
    static readonly SCHEDULER_VIEW    = "scheduler:view";
    static readonly SCHEDULER_CONTROL = "scheduler:control";
    static readonly SCHEDULER_RUN     = "scheduler:run";

    // -- Task catalog (entity `task-definition`) -----------------------------
    static readonly TASK_DEFINITION_ACCESS  = "task-definition:access";
    static readonly TASK_DEFINITION_LIST    = "task-definition:list";
    static readonly TASK_DEFINITION_VIEW    = "task-definition:view";
    static readonly TASK_DEFINITION_CREATE  = "task-definition:create";
    static readonly TASK_DEFINITION_UPDATE  = "task-definition:update";
    static readonly TASK_DEFINITION_DELETE  = "task-definition:delete";
    static readonly TASK_DEFINITION_CONTROL = "task-definition:control";

    // -- Runner registry (entity `runner`) -----------------------------------
    static readonly RUNNER_ACCESS  = "runner:access";
    static readonly RUNNER_LIST    = "runner:list";
    static readonly RUNNER_VIEW    = "runner:view";
    static readonly RUNNER_CREATE  = "runner:create";
    static readonly RUNNER_UPDATE  = "runner:update";
    static readonly RUNNER_DELETE  = "runner:delete";
    static readonly RUNNER_CONTROL = "runner:control";

    // -- Capability registry (entity `capability`) ---------------------------
    static readonly CAPABILITY_ACCESS  = "capability:access";
    static readonly CAPABILITY_LIST    = "capability:list";
    static readonly CAPABILITY_VIEW    = "capability:view";
    static readonly CAPABILITY_CREATE  = "capability:create";
    static readonly CAPABILITY_UPDATE  = "capability:update";
    static readonly CAPABILITY_DELETE  = "capability:delete";
    static readonly CAPABILITY_CONTROL = "capability:control";

    // -- Data sources (entity `datasource`) ----------------------------------
    static readonly DATASOURCE_ACCESS = "datasource:access";
    static readonly DATASOURCE_LIST   = "datasource:list";
    static readonly DATASOURCE_VIEW   = "datasource:view";
    static readonly DATASOURCE_CREATE = "datasource:create";
    static readonly DATASOURCE_UPDATE = "datasource:update";
    static readonly DATASOURCE_DELETE = "datasource:delete";

    // == Nucleus-owned colon caps (shared libs behind product screens) =======
    // Owned by the nucleus services and shared verbatim across tenants — the
    // group→caps expansion grants them to the console groups. NOT the tasksense
    // entity:action naming above.

    // -- Users (nucleus-user) ------------------------------------------------
    static readonly USER_LIST           = "user:list";
    static readonly USER_VIEW           = "user:view";
    static readonly USER_CREATE         = "user:create";
    static readonly USER_UPDATE         = "user:update";
    static readonly USER_DELETE         = "user:delete";
    static readonly USER_BLOCK          = "user:block";
    static readonly USER_UNBLOCK        = "user:unblock";
    static readonly USER_RESET_PASSWORD = "user:reset-password";
    static readonly USER_REPORT         = "user:report";

    // -- Access requests (nucleus-user) --------------------------------------
    static readonly ACCESS_REQUEST_LIST    = "access-request:list";
    static readonly ACCESS_REQUEST_APPROVE = "access-request:approve";
    static readonly ACCESS_REQUEST_REJECT  = "access-request:reject";

    // -- Audit / Mail (nucleus-audit, nucleus-mail) --------------------------
    static readonly AUDIT_LIST          = "audit:list";
    static readonly AUDIT_VIEW          = "audit:view";
    static readonly AUDIT_REPORT        = "audit:report";
    static readonly MAIL_REPORT         = "mail:report";
    static readonly MAIL_TEMPLATE_LIST  = "mail-template:list";

    // -- System properties (nucleus-system-property) -------------------------
    static readonly SYSTEM_PROPERTY_LIST   = "system-property:list";
    static readonly SYSTEM_PROPERTY_VIEW   = "system-property:view";
    static readonly SYSTEM_PROPERTY_UPDATE = "system-property:update";
    static readonly SYSTEM_PROPERTY_REPORT = "system-property:report";

    // -- Reference Data (nucleus-reference-data) -----------------------------
    static readonly REFERENCE_DATA_LIST   = "reference-data:list";
    static readonly REFERENCE_DATA_VIEW   = "reference-data:view";
    static readonly REFERENCE_DATA_CREATE = "reference-data:create";
    static readonly REFERENCE_DATA_UPDATE = "reference-data:update";
    static readonly REFERENCE_DATA_DELETE = "reference-data:delete";
    static readonly REFERENCE_DATA_REPORT = "reference-data:report";
    static readonly REFERENCE_DATA_MANAGE = "reference-data:manage";

    // -- Connections (nucleus-connectors) ------------------------------------
    static readonly CONNECTIONS_ACCESS = "connections:access";
    static readonly CONNECTIONS_LIST   = "connections:list";
    static readonly CONNECTIONS_VIEW   = "connections:view";
    static readonly CONNECTIONS_CREATE = "connections:create";
    static readonly CONNECTIONS_UPDATE = "connections:update";
    static readonly CONNECTIONS_DELETE = "connections:delete";
    static readonly CONNECTIONS_TEST   = "connections:test";

    // -- IP Blocklist (nucleus gateway) --------------------------------------
    static readonly IP_BLACKLIST_LIST    = "ip-blacklist:list";
    static readonly IP_BLACKLIST_BLOCK   = "ip-blacklist:block";
    static readonly IP_BLACKLIST_UNBLOCK = "ip-blacklist:unblock";

    // -- Org units (nucleus-user) --------------------------------------------
    static readonly ORG_UNIT_LIST   = "org-unit:list";
    static readonly ORG_UNIT_VIEW   = "org-unit:view";
    static readonly ORG_UNIT_CREATE = "org-unit:create";
    static readonly ORG_UNIT_UPDATE = "org-unit:update";
    static readonly ORG_UNIT_DELETE = "org-unit:delete";
}
